import {
  Box,
  Paper,
  Typography,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TableSortLabel,
  MenuItem,
} from '@mui/material';
import { FilterCell, TableFilterInput } from '../user-dash/filterCell';
import { ExportButtonSx } from './dqShared';
import { formatDisplayDate, formatDateTime, LAST_ANSWER_DATE_FILTER_OPTIONS, TABLE_SUBSCRIPTION_FILTER_OPTIONS, TRUE_FALSE_FILTER_OPTIONS } from './dqUtils';

const HEADER_CELL_SX = { fontWeight: 'bold', color: '#003ABA' };

const COLUMNS = [
  { id: 'name', label: 'Name', sortable: false, placeholder: 'Search Name' },
  { id: 'email', label: 'Email', sortable: false, placeholder: 'Search Email' },
  {
    id: 'firstAnswerDate',
    label: 'First answer date',
    sortable: true,
    filterType: 'lastAnswerDate',
    filterOptions: LAST_ANSWER_DATE_FILTER_OPTIONS,
  },
  {
    id: 'lastAnswerDate',
    label: 'Last answer date',
    sortable: true,
    filterType: 'lastAnswerDate',
    filterOptions: LAST_ANSWER_DATE_FILTER_OPTIONS,
  },
  { id: 'currentStreak', label: 'Current streak', sortable: true, align: 'right', placeholder: 'Search Current streak' },
  { id: 'totalQuestionsAnswered', label: 'Total answered', sortable: true, align: 'right', placeholder: 'Search Total answered' },
  { id: 'totalCorrectAnswers', label: 'Total correct', sortable: true, align: 'right', placeholder: 'Search Total correct' },
  { id: 'accuracy', label: 'Accuracy (%)', sortable: true, align: 'right', placeholder: 'Search Accuracy' },
  { id: 'leaderboardRank', label: 'Leaderboard rank', sortable: true, align: 'right', placeholder: 'Search Leaderboard rank' },
  { id: 'subscriptionType', label: 'Subscription', sortable: true, filterType: 'select', filterOptions: TABLE_SUBSCRIPTION_FILTER_OPTIONS },
  { id: 'firstPurchaseDate', label: 'First purchase date', sortable: true, filterType: 'lastAnswerDate', filterOptions: LAST_ANSWER_DATE_FILTER_OPTIONS },
  { id: 'subscribedAfterFirstAnswer', label: 'Subscribed after first answer', sortable: true, align: 'center', filterType: 'select', filterOptions: TRUE_FALSE_FILTER_OPTIONS },
  { id: 'daysToFirstPurchase', label: 'Days to first purchase', sortable: true, align: 'right' },
];

const DqUsersTable = ({
  users,
  total,
  page,
  rowsPerPage,
  pageSizeOptions,
  filters,
  sort,
  order,
  tableLoading,
  onSearchChange,
  onSortChange,
  onPageChange,
  onRowsPerPageChange,
  onExport,
}) => (
  <Paper sx={{ mt: 3 }}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 2,
        mt: 1,
      }}
    >
      <Typography variant="h6" sx={{ fontSize: 20 }}>
        Table of Users Who Answered Daily Questions At Least Once
      </Typography>
      <Button
        variant="contained"
        onClick={onExport}
        disabled={tableLoading}
        sx={ExportButtonSx}
        startIcon={
          <Box component="img" src="/icons/export-csv.svg" alt="export" sx={{ width: 40, height: 24 }} />
        }
      >
        Export Users
      </Button>
    </Box>

    <TableContainer
      component={Paper}
      sx={{ minHeight: 400, maxHeight: 700, overflowX: 'auto', overflowY: 'auto', width: '100%' }}
    >
      <Table
        stickyHeader
        size="small"
        sx={{
          tableLayout: 'auto',
          width: 'max-content',
          minWidth: '100%',
          '& .MuiTableCell-root': {
            borderRight: '1px solid #e0e0e0',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'clip',
          },
        }}
      >
        <TableHead>
          <TableRow>
            {COLUMNS.map((col) => (
              <FilterCell key={`filter-${col.id}`}>
                {col.filterType === 'select' || col.filterType === 'lastAnswerDate' ? (
                  <TableFilterInput
                    select
                    value={filters[col.id] ?? ''}
                    onChange={(e) => onSearchChange(col.id, e.target.value)}
                    SelectProps={{ displayEmpty: true }}
                  >
                    {col.filterOptions.map((opt) => (
                      <MenuItem key={opt.value || 'all'} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TableFilterInput>
                ) : col.placeholder ? (
                  <TableFilterInput
                    placeholder={col.placeholder}
                    value={filters[col.id] ?? ''}
                    onChange={(e) => onSearchChange(col.id, e.target.value)}
                  />
                ) : null}
              </FilterCell>
            ))}
          </TableRow>

          <TableRow>
            {COLUMNS.map((col) => (
              <TableCell
                key={col.id}
                align={col.align || 'left'}
                sortDirection={sort === col.id ? order : false}
                sx={HEADER_CELL_SX}
              >
                {col.sortable ? (
                  <TableSortLabel
                    active={sort === col.id}
                    direction={sort === col.id ? order : 'asc'}
                    onClick={() => onSortChange(col.id)}
                    sx={{
                      '& .MuiTableSortLabel-icon': {
                        color: '#003ABA !important',
                      },
                    }}
                  >
                    {col.label}
                  </TableSortLabel>
                ) : (
                  col.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {tableLoading && users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMNS.length} align="center">
                Loading users…
              </TableCell>
            </TableRow>
          ) : users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMNS.length} align="center">
                No users found
              </TableCell>
            </TableRow>
          ) : (
            users.map((row) => (
              <TableRow key={row.userId}>
                <TableCell>{row.name || '-'}</TableCell>
                <TableCell title={row.email || ''}>{row.email || '-'}</TableCell>
                <TableCell>{formatDisplayDate(row.firstAnswerDate)}</TableCell>
                <TableCell>{formatDisplayDate(row.lastAnswerDate)}</TableCell>
                <TableCell align="right">{row.currentStreak ?? 0}</TableCell>
                <TableCell align="right">{row.totalQuestionsAnswered ?? 0}</TableCell>
                <TableCell align="right">{row.totalCorrectAnswers ?? 0}</TableCell>
                <TableCell align="right">{row.accuracy ?? 0}</TableCell>
                <TableCell align="right">{row.leaderboardRank ?? '-'}</TableCell>
                <TableCell>{row.subscriptionType || '-'}</TableCell>
                <TableCell>{formatDateTime(row.firstPurchaseDate)}</TableCell>
                <TableCell align="center">
                  {row.subscribedAfterFirstAnswer === true ? 'True' : 'False'}
                </TableCell>
                <TableCell align="right">
                  {row.subscribedAfterFirstAnswer === true && row.daysToFirstPurchase != null
                    ? row.daysToFirstPurchase
                    : '-'}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>

    <TablePagination
      component="div"
      count={total}
      page={page}
      onPageChange={(_e, newPage) => onPageChange(newPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
      rowsPerPageOptions={pageSizeOptions}
    />
  </Paper>
);

export default DqUsersTable;
